import {
  BODY_ICONS,
  BODY_LABELS,
  lookLabel,
  LOOK_COLORS,
  namesFor,
  randomLook,
  withBody,
} from '../content/looks';
import type { Random } from '../core/Random';
import type { IconToken } from '../icons/index';
import {
  BEARD_STYLES,
  BODY_TYPES,
  BOTTOM_STYLES,
  HAIR_STYLES,
  HAT_STYLES,
  SHOE_STYLES,
  TOP_STYLES,
  type BodyType,
  type CharacterLook,
} from '../state/types';
import { clear, h } from './dom';

/** What part of the character a style tile's picture shows. */
export type ThumbFrame = 'head' | 'torso' | 'legs' | 'feet';

export interface CreatorHost {
  rng: Random;
  /** Dress the character standing in the yard. */
  onLook(look: CharacterLook): void;
  /** Spin the character (dragging across the preview). */
  onTurn(radians: number): void;
  onBack(): void;
  onDone(name: string, look: CharacterLook): void;
  /** A picture of this look (as an image URL), framed on part of the body. */
  thumb(look: CharacterLook, frame: ThumbFrame): string;
}

type TabId = 'body' | 'hair' | 'face' | 'top' | 'bottom' | 'shoes' | 'hat';

const TABS: ReadonlyArray<{ id: TabId; icon: IconToken; label: string }> = [
  { id: 'body', icon: ':flex:', label: 'Body' },
  { id: 'hair', icon: ':haircut:', label: 'Hair' },
  { id: 'face', icon: ':shades:', label: 'Face' },
  { id: 'top', icon: ':tshirt:', label: 'Top' },
  { id: 'bottom', icon: ':jeans:', label: 'Bottoms' },
  { id: 'shoes', icon: ':croc:', label: 'Shoes' },
  { id: 'hat', icon: ':cap:', label: 'Hat' },
];

/** Thumbnails drawn per animation frame (each one renders the character once). */
const THUMBS_PER_FRAME = 2;
const THUMB_CACHE = 160;

/**
 * Building your redneck, after New Game: the character stands close-up in the yard on a
 * turntable (drag to spin), and the panel dresses them. Every style shows as a little
 * picture of *you* wearing it.
 */
export class CharacterCreator {
  readonly el: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly tabBody: HTMLDivElement;
  private look: CharacterLook;
  private name = '';
  private tab: TabId = 'hair';
  /** What you'd built if you backed out to the menu (it's still here when you come back). */
  private draft: { look: CharacterLook; name: string } | null = null;
  private readonly thumbs = new Map<string, string>();
  private queue: Array<() => void> = [];
  private pump: number | null = null;

  constructor(
    parent: HTMLElement,
    private readonly host: CreatorHost,
    initial: CharacterLook,
  ) {
    this.look = { ...initial };
    const stage = h(
      'div',
      { class: 'creator-stage' },
      h('div', { class: 'creator-spin', text: ':repeat: Drag to spin around' }),
    );
    this.bindSpin(stage);
    this.tabBody = h('div', { class: 'creator-tab-body' });
    this.panel = h('div', { class: 'creator-panel cardboard interactive' });
    this.el = h('div', { class: 'creator-screen', hidden: true }, stage, this.panel);
    this.el.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.host.onBack();
      }
    });
    parent.append(this.el);
  }

  get isOpen(): boolean {
    return !this.el.hidden;
  }

  /** Where the panel sits on screen (the camera frames the character around it). */
  panelRect(): DOMRect | null {
    return this.el.hidden ? null : this.panel.getBoundingClientRect();
  }

  /** Opens where you left off, or with a fresh character (and a random name to go with them). */
  show(fresh: CharacterLook): void {
    const draft = this.draft;
    this.look = draft ? draft.look : { ...fresh, shades: fresh.shades ?? false };
    this.name = draft ? draft.name : this.host.rng.pick(namesFor(this.look));
    this.tab = 'hair';
    this.el.hidden = false;
    this.host.onLook(this.look);
    this.render();
    this.panel.querySelector<HTMLElement>('.who button.on')?.focus({ preventScroll: true });
  }

  hide(): void {
    if (!this.el.hidden) this.draft = { look: this.look, name: this.name };
    this.el.hidden = true;
    this.queue = [];
    if (this.pump !== null) cancelAnimationFrame(this.pump);
    this.pump = null;
  }

  // --- changes -------------------------------------------------------------------

  private set(patch: Partial<CharacterLook>, rerender = true): void {
    this.look = { ...this.look, ...patch };
    this.host.onLook(this.look);
    if (rerender) this.render();
  }

  /** A man's build or a woman's: a fresh outfit to start from, and a name to match. */
  private setBody(body: BodyType): void {
    if (body === (this.look.body ?? 'man')) return;
    const wasDefault = namesFor(this.look).includes(this.name.trim());
    this.look = withBody(this.look, body);
    if (wasDefault || !this.name.trim()) this.name = this.host.rng.pick(namesFor(this.look));
    this.host.onLook(this.look);
    this.render();
  }

  private randomize(): void {
    this.look = randomLook(this.host.rng, this.look);
    this.name = this.host.rng.pick(namesFor(this.look));
    this.host.onLook(this.look);
    this.render();
  }

  private done(): void {
    const name = this.name.trim() || 'Bubba';
    const look = this.look;
    // Moved in: next time New Game starts from scratch.
    this.hide();
    this.draft = null;
    this.host.onDone(name, look);
  }

  private bindSpin(stage: HTMLElement): void {
    let last: number | null = null;
    stage.addEventListener('pointerdown', (e) => {
      last = e.clientX;
      stage.setPointerCapture(e.pointerId);
      stage.classList.add('spinning');
    });
    stage.addEventListener('pointermove', (e) => {
      if (last === null) return;
      this.host.onTurn((e.clientX - last) * 0.012);
      last = e.clientX;
    });
    const up = (): void => {
      last = null;
      stage.classList.remove('spinning');
    };
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
  }

  // --- thumbnails ------------------------------------------------------------------

  /** Fills `img` with a picture of `look`, now if it's cached, otherwise a frame or two from now. */
  private thumbInto(img: HTMLImageElement, look: CharacterLook, frame: ThumbFrame): void {
    const key = `${frame}|${JSON.stringify(look)}`;
    const hit = this.thumbs.get(key);
    if (hit) {
      img.src = hit;
      return;
    }
    this.queue.push(() => {
      if (!img.isConnected) return;
      let url = this.thumbs.get(key);
      if (!url) {
        url = this.host.thumb(look, frame);
        this.thumbs.set(key, url);
        if (this.thumbs.size > THUMB_CACHE) this.thumbs.delete(this.thumbs.keys().next().value as string);
      }
      img.src = url;
    });
    if (this.pump === null) this.pump = requestAnimationFrame(this.drain);
  }

  private drain = (): void => {
    this.pump = null;
    for (let i = 0; i < THUMBS_PER_FRAME && this.queue.length; i++) this.queue.shift()?.();
    if (this.queue.length && !this.el.hidden) this.pump = requestAnimationFrame(this.drain);
  };

  // --- rendering ---------------------------------------------------------------------

  private render(): void {
    const scroll = this.tabBody.scrollTop;
    this.queue = [];
    clear(this.panel);
    const l = this.look;

    const who = h('div', { class: 'who', attrs: { role: 'radiogroup', 'aria-label': 'Who are you?' } });
    for (const body of BODY_TYPES) {
      const on = (l.body ?? 'man') === body;
      who.append(
        h(
          'button',
          {
            class: on ? 'on' : '',
            attrs: { role: 'radio', 'aria-checked': String(on) },
            on: { click: () => this.setBody(body) },
          },
          h('span', { class: 'who-icon', text: BODY_ICONS[body] }),
          h('span', { text: BODY_LABELS[body] }),
        ),
      );
    }

    const nameInput = h('input', {
      attrs: {
        type: 'text',
        maxlength: '18',
        value: this.name,
        'aria-label': 'Your name',
        spellcheck: 'false',
      },
    });
    nameInput.addEventListener('input', () => (this.name = nameInput.value));
    nameInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.done();
    });
    const nameRow = h(
      'div',
      { class: 'name-row' },
      nameInput,
      h('button', {
        class: 'dice-btn',
        text: ':dice:',
        title: 'Random name',
        attrs: { 'aria-label': 'Random name' },
        on: {
          click: () => {
            const names = namesFor(this.look).filter((n) => n !== this.name);
            this.name = this.host.rng.pick(names);
            nameInput.value = this.name;
          },
        },
      }),
    );

    const tabs = h('div', { class: 'creator-tabs', attrs: { role: 'tablist' } });
    for (const t of TABS) {
      const on = t.id === this.tab;
      tabs.append(
        h(
          'button',
          {
            class: on ? 'on' : '',
            attrs: { role: 'tab', 'aria-selected': String(on) },
            on: {
              click: () => {
                this.tab = t.id;
                this.render();
                this.tabBody.scrollTop = 0;
              },
            },
          },
          h('span', { class: 'tab-icon', text: t.icon }),
          h('span', { class: 'tab-label', text: t.label }),
        ),
      );
    }

    clear(this.tabBody);
    this.tabBody.append(...this.tabContent(this.tab));

    this.panel.append(
      h('h2', { text: 'Build Your Redneck' }),
      who,
      h('label', { class: 'creator-label', text: 'Name' }),
      nameRow,
      tabs,
      this.tabBody,
      h(
        'div',
        { class: 'creator-actions' },
        h('button', { class: 'tape', text: ':back: Back', on: { click: () => this.host.onBack() } }),
        h('button', { class: 'tape', text: ':dice: Randomize', on: { click: () => this.randomize() } }),
        h(
          'button',
          { class: 'tape primary move-in', on: { click: () => this.done() } },
          h('span', { text: ':trailer: Move In' }),
        ),
      ),
    );
    this.tabBody.scrollTop = scroll;
  }

  private section(label: string, ...children: HTMLElement[]): HTMLElement {
    return h(
      'div',
      { class: 'creator-section' },
      h('div', { class: 'creator-label', text: label }),
      ...children,
    );
  }

  /** Big picture tiles for each style of something. */
  private styles<K extends keyof CharacterLook>(
    key: K,
    values: ReadonlyArray<CharacterLook[K]>,
    label: (v: CharacterLook[K]) => string,
    frame: ThumbFrame,
  ): HTMLElement {
    const grid = h('div', { class: 'style-grid', attrs: { role: 'radiogroup' } });
    for (const v of values) {
      const on = this.look[key] === v;
      const img = h('img', { attrs: { alt: '', draggable: 'false' } });
      // Hairdos show bareheaded; a hat would hide what you're picking.
      const hat = key === 'hair' ? { hat: 'none' as const } : {};
      this.thumbInto(img, { ...this.look, ...hat, [key]: v }, frame);
      grid.append(
        h(
          'button',
          {
            class: `style-tile ${on ? 'on' : ''}`,
            attrs: { role: 'radio', 'aria-checked': String(on), 'aria-label': label(v) },
            on: { click: () => this.set({ [key]: v } as Partial<CharacterLook>) },
          },
          img,
          h('span', { text: label(v) }),
        ),
      );
    }
    return grid;
  }

  private swatches(
    key: 'skin' | 'hairColor' | 'topColor' | 'bottomColor' | 'shoeColor' | 'hatColor',
    colors: readonly string[],
    name: string,
  ): HTMLElement {
    const row = h('div', { class: 'swatches', attrs: { role: 'radiogroup', 'aria-label': name } });
    for (const c of colors) {
      const on = this.look[key] === c;
      row.append(
        h('button', {
          class: `swatch ${on ? 'on' : ''}`,
          style: { background: c },
          attrs: { role: 'radio', 'aria-checked': String(on), 'aria-label': `${name} ${c}` },
          on: { click: () => this.set({ [key]: c }) },
        }),
      );
    }
    return row;
  }

  private slider(
    label: string,
    key: 'belly' | 'height',
    min: number,
    max: number,
    ends: [string, string],
  ): HTMLElement {
    const input = h('input', {
      attrs: {
        type: 'range',
        min: String(min),
        max: String(max),
        step: '0.01',
        value: String(this.look[key]),
        'aria-label': label,
      },
    });
    // The character updates as you drag; the tiles catch up when you let go.
    input.addEventListener('input', () => this.set({ [key]: Number(input.value) }, false));
    input.addEventListener('change', () => this.render());
    return this.section(
      label,
      h('div', { class: 'slider-row' }, h('span', { text: ends[0] }), input, h('span', { text: ends[1] })),
    );
  }

  private tabContent(tab: TabId): HTMLElement[] {
    const l = this.look;
    switch (tab) {
      case 'body':
        return [
          this.section('Skin', this.swatches('skin', LOOK_COLORS.skin, 'Skin')),
          this.slider('Beer belly', 'belly', 0, 1, ['Skinny', ':beer: Proud']),
          this.slider('Height', 'height', 0.88, 1.1, ['Short', 'Tall']),
        ];
      case 'hair':
        return [
          this.section('Hairdo', this.styles('hair', HAIR_STYLES, lookLabel.hair, 'head')),
          this.section('Color', this.swatches('hairColor', LOOK_COLORS.hair, 'Hair color')),
        ];
      case 'face':
        return [
          this.section('Facial hair', this.styles('beard', BEARD_STYLES, lookLabel.beard, 'head')),
          this.section(
            'Shades',
            this.styles('shades', [false, true], (v) => (v ? 'Gas station shades' : 'No shades'), 'head'),
          ),
        ];
      case 'top':
        return [
          this.section(
            'Top',
            this.styles('top', TOP_STYLES, (v) => lookLabel.top(v, l), 'torso'),
          ),
          this.section('Color', this.swatches('topColor', LOOK_COLORS.cloth, 'Top color')),
        ];
      case 'bottom':
        return [
          this.section(
            'Bottoms',
            this.styles('bottom', BOTTOM_STYLES, (v) => lookLabel.bottom(v, l), 'legs'),
          ),
          this.section('Color', this.swatches('bottomColor', LOOK_COLORS.denim, 'Bottoms color')),
        ];
      case 'shoes':
        return [
          this.section('Shoes', this.styles('shoes', SHOE_STYLES, lookLabel.shoes, 'feet')),
          this.section('Color', this.swatches('shoeColor', LOOK_COLORS.shoes, 'Shoe color')),
        ];
      case 'hat':
        return [
          this.section('Hat', this.styles('hat', HAT_STYLES, lookLabel.hat, 'head')),
          this.section('Color', this.swatches('hatColor', LOOK_COLORS.hat, 'Hat color')),
        ];
    }
  }
}
